import { Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { CountryPhoneInput } from 'components/CountryPhoneInput/CountryPhoneInput'
import { Margin } from 'components/ui'
import { ErrorText } from 'components/ui/Text/Text'
import { Divider, FormItem, Input } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { snilsPipe } from 'utils/pipes'
import { EMAIL_REGEXP, TELEGRAM_REGEXP } from 'shared/constants/regExp'

import { countries } from '../../../data'
import { ProfileViewModel } from '../../../stores/models/ProfileViewModel'
import { Citizenship, UserModuleKeys } from '../../../types'

type ContactInfoStepProps = {
  profile: ProfileViewModel
  citizenship: Citizenship
}

export const ContactInfoStep = observer(({ profile, citizenship }: ContactInfoStepProps) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const getKey = (key: string) => `${UserModuleKeys.contactInfoStep}.${key}`

  useEffect(() => {
    form.setFieldValue('snils', snilsPipe(profile.snils))
    form.setFieldValue('telegramLogin', profile.telegramLogin)
    form.setFieldValue('email', profile.email)
  }, [])

  return (
    <Form form={form} layout="vertical" style={{ maxWidth: 600, marginTop: 50 }}>
      {citizenship === Citizenship.RU && (
        <FormItem
          label={t(getKey('СНИЛС'))}
          name={'snils'}
          rules={[
            { required: true, message: t(getSharedKey('required')) },
            {
              min: 11,
              message: t(getSharedKey('minLength'), { count: 11 }),
            },
          ]}
          normalize={(value) => snilsPipe(value?.replace(/[^0-9]/g, ''))}
        >
          <Input value={snilsPipe(profile.snils)} onChange={(e) => profile.setSnils(e.target.value)} maxLength={14} />
        </FormItem>
      )}

      <FormItem
        label={t(getKey('Номер телефона для связи'))}
        name={'phone'}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
      >
        <CountryPhoneInput
          phonePrefix={profile.additionalPhoneModel.phonePrefix}
          phone={profile.additionalPhoneModel.phone}
          country={profile.additionalPhoneModel.selectedCountry.country}
          setPhone={profile.additionalPhoneModel.setPhone}
          setSelectedCountry={(value) => profile.additionalPhoneModel.setSelectedCountry(value ?? countries[0].value)}
        />
      </FormItem>
      {!profile.additionalPhoneModel.isValidPhone && (
        <Margin mt={-10}>
          <ErrorText error>{t(getSharedKey('required'))}</ErrorText>
        </Margin>
      )}

      <FormItem
        label={t(getKey('Публичное имя в Telegram'))}
        name={'telegramLogin'}
        rules={[
          { required: true, message: t(getSharedKey('required')) },
          {
            validator: () => {
              if (!TELEGRAM_REGEXP.test(profile.telegramLogin)) {
                return Promise.reject(new Error('Необходимо ввести полное имя, включая символ @'))
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <Input
          value={profile.telegramLogin}
          onChange={(e) => profile.setTelegramLogin(e.target.value)}
          placeholder="@username"
        />
      </FormItem>

      <FormItem
        label={t(getKey('E-mail'))}
        name={'email'}
        rules={[
          { required: true, message: t(getSharedKey('required')) },
          {
            validator: () => {
              if (!EMAIL_REGEXP.test(profile.email)) {
                return Promise.reject(
                  new Error(
                    'Неверный формат ввода. Необходимо ввести адрес, символ @, почтовый домен, например example@yandex.ru',
                  ),
                )
              } else {
                return Promise.resolve()
              }
            },
          },
        ]}
      >
        <Input
          placeholder="example@yandex.ru"
          value={profile.email}
          onChange={(e) => profile.setEmail(e.target.value)}
        />
      </FormItem>

      <Divider />
    </Form>
  )
})
